// ─────────────────────────────────────────────
//  Alerts domain — pure logic for the Emergency / Urgency system.
//  No Firestore, no React: just data shaping so it is unit-testable.
//
//  Two alert TYPES share the `alerts` collection:
//    • emergency — LOUD. Broadcast to every active supervising doctor + the
//      AIC of the day. Drives board visuals (red room box + blink) and a
//      recipient banner. Cleared by the supervising doctor (or AIC/admin).
//    • urgency   — SILENT. Private ping to the ONE doctor assigned to the
//      room. No board visual, no blink, no sound. Seen only by that doctor.
//
//  Board visuals are DERIVED from active alerts (never stored on the room
//  doc): a room is "in emergency" while an emergency alert for it is live.
//  That auto-clears on resolve and survives a sender disconnect.
// ─────────────────────────────────────────────

import { dayKey } from "./dayKey";

export const ALERT_TYPES = {
  EMERGENCY: "emergency",
  URGENCY: "urgency",
  // break_request — a provider in a room asks for someone to come cover a break.
  // Drives a blinking YELLOW room on every board + TV (not loud, no alarm), and
  // shows in the room doctor's + every free provider's My Rooms. Cleared when a
  // provider takes over the break (or the original returns and ends it).
  BREAK_REQUEST: "break_request",
  // room_condition — QUIET informational ping (Phase 5.2): a CRNA posted a
  // room condition (delayed start, equipment issue…) or a handoff event fired
  // (follow-up set / provider arrived). Targets the room's supervising doctor.
  // No alarm, no blink, no board wash — the room card's ConditionChip /
  // FollowUpBanner is the visual; this alert feeds the doctor's notification
  // strip. Cleared cooperatively (either party) or when the condition clears.
  ROOM_CONDITION: "room_condition",
};

export const TARGET_TYPES = {
  EMERGENCY_BROADCAST: "emergency_broadcast",
  ROOM_DOCTOR: "room_doctor",
  BREAK_BROADCAST: "break_broadcast",
};

// An alert is "live" (still demands attention / drives visuals) while it is
// sent or acknowledged. Resolved alerts drop out of the live listener query.
const LIVE_STATUSES = ["sent", "acknowledged"];
export const isLiveAlert = (a) => !!a && LIVE_STATUSES.includes(a.status);

// ── EXPIRY ───────────────────────────────────
//
// ⚠️ NOTHING EVER EXPIRED AN ALERT. `listenToAlerts` filters on facility and
// status only — no date bound — and the status only changes when a human
// presses Resolve. So an urgency nobody closed stayed live FOREVER and arrived
// on every board every day afterwards. Live on 2026-08-18: a WC E2 urgency
// raised on 2026-06-30 was still rendering "Urgency active — acknowledge",
// 1167 hours old, beside a real room. Eleven of them across the facility.
//
// That is not a cosmetic bug. It spends the alarm colour on nothing, and the
// one thing an OR board cannot afford is staff learning that amber means
// "probably old" ([[alarm-colour-is-finite]]).
//
// THE OR DAY RUNS 07:00 → 07:00, not midnight. That is the boundary the rest of
// this app already works in: the on-call shift is 7a→7a and the sheet importer
// writes 07:00→07:00 for a standing watch. An alert belongs to the OR day it
// was raised on; once the board has rolled into the next one, the team that
// could have answered it has gone home.
export const ALERT_DAY_START_HOUR = 7;

// ⚠️ A FLOOR, SO AN ALERT RAISED NEAR THE BOUNDARY IS NOT BORN DEAD. Without it
// an urgency at 06:58 would expire two minutes later, which is the opposite of
// the point. Under an hour old, an alert is live whatever day it belongs to.
export const ALERT_MIN_LIFETIME_MS = 60 * 60 * 1000;

// The OR-day key for an instant: shift back to the 07:00 boundary, then take the
// facility's calendar day. Same `dayKey` the board keys its documents with, so
// "which day is this" has ONE answer across the app.
export function alertOrDayKey(date, tz) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return dayKey(new Date(date.getTime() - ALERT_DAY_START_HOUR * 3600 * 1000), tz);
}

// True when this alert belongs to an OR day the board has already left behind.
//
// An alert with NO timestamp is NOT stale: `sentAt` is a serverTimestamp() and
// is null on the raising device until the write round-trips, so treating an
// absent time as old would blank the alert on the very phone that just sent it.
export function isStaleAlert(alert, now = new Date(), tz) {
  const sent = alertSentAt(alert);
  if (!sent) return false;
  if (now.getTime() - sent.getTime() < ALERT_MIN_LIFETIME_MS) return false;
  return alertOrDayKey(sent, tz) !== alertOrDayKey(now, tz);
}

// What the boards should actually react to: live AND from today's OR day.
export const isActionableAlert = (a, now = new Date(), tz) =>
  isLiveAlert(a) && !isStaleAlert(a, now, tz);

// ── Targeting ────────────────────────────────
// Emergency recipients = the AIC of the day + every supervising doctor on
// today's roster + (explicitly) the room's own supervising doctor. The room
// doctor is almost always already a roster supervisor, but we add them
// defensively so the CRNA→supervisor relationship can never be dropped from an
// emergency target list. Deduped, falsy-stripped. The sender is a clinical
// provider, not a doctor, so they are not in this set; no self-exclusion needed.
export function resolveEmergencyTargets(roster, roomDoctorId = null) {
  const supervisorIds = (roster?.supervisors || []).map((s) => s?.id);
  const ids = [roster?.aicId, ...supervisorIds, roomDoctorId].filter(Boolean);
  return Array.from(new Set(ids));
}

// The supervising doctor of a room. Prefer the live room doc; fall back to
// today's assignment attending.
export function resolveRoomDoctorId(room, assignment) {
  return room?.supervisingDoctorId || assignment?.attendingId || null;
}

// The CRNA/AA staffing a room. Prefer the live room doc's provider; fall back to
// today's assignment AHP.
export function resolveRoomCrnaId(room, assignment) {
  return room?.providerId || assignment?.ahpId || null;
}

// Urgency recipients = the room's supervising doctor AND its CRNA. The ping is
// private to those two people: both see THEIR room blink (the doctor so they
// respond, the CRNA as confirmation their ping landed). It never drives the
// board for anyone else. A doctor is required (the ping has to reach someone);
// the CRNA is included when known. After the CRNA confirms (acknowledged), the
// ping narrows to the doctor alone (see urgenciesForUser).
export function resolveUrgencyTargets(room, assignment) {
  const doctorId = resolveRoomDoctorId(room, assignment);
  const crnaId = resolveRoomCrnaId(room, assignment);
  const targetIds = doctorId ? Array.from(new Set([doctorId, crnaId].filter(Boolean))) : [];
  return { doctorId, crnaId, targetIds };
}

// ── Board-visual derivation ──────────────────
// Set of roomIds with a LIVE emergency. Urgencies never appear here (silent).
export function emergencyRoomIds(alerts = []) {
  const ids = alerts
    .filter((a) => a.type === ALERT_TYPES.EMERGENCY && isLiveAlert(a) && a.roomId)
    .map((a) => a.roomId);
  return new Set(ids);
}

// Live emergencies the given user is a recipient of (drives their banner).
export function emergenciesForUser(alerts = [], uid) {
  return alerts.filter(
    (a) =>
      a.type === ALERT_TYPES.EMERGENCY &&
      isLiveAlert(a) &&
      Array.isArray(a.targetIds) &&
      a.targetIds.includes(uid)
  );
}

// Live urgencies this user should see in My Rooms. Targets = the room's doctor
// + CRNA. Once the CRNA CONFIRMS (status acknowledged), the ping narrows to the
// supervising doctor only — the CRNA no longer sees it (D: "when a CRNA confirms
// an urgency, only their supervising doctor sees it"). Alerts without a stored
// roomDoctorId keep the old broad behavior (backward compatible).
export function urgenciesForUser(alerts = [], uid) {
  return alerts.filter(
    (a) =>
      a.type === ALERT_TYPES.URGENCY &&
      isLiveAlert(a) &&
      Array.isArray(a.targetIds) &&
      a.targetIds.includes(uid) &&
      !(a.status === "acknowledged" && a.roomDoctorId && uid !== a.roomDoctorId)
  );
}

// Live urgencies that should RING (tone + vibration) for this viewer right
// now: they target one of the viewer's identities, were NOT sent by the
// viewer (the CRNA who pressed the button gets the visual, not the alarm),
// and haven't already been alarmed this session (seenIds).
export function urgenciesNeedingAlarm(alerts = [], { identity, seenIds } = {}) {
  const ids = identity || new Set();
  const seen = seenIds || new Set();
  return alerts.filter(
    (a) =>
      a &&
      a.type === ALERT_TYPES.URGENCY &&
      isLiveAlert(a) &&
      a.id &&
      !seen.has(a.id) &&
      Array.isArray(a.targetIds) &&
      a.targetIds.some((t) => ids.has(t)) &&
      !(a.senderId && ids.has(a.senderId))
  );
}

// Live emergencies this user should be SHOWN as a popup — targets them and they
// have not yet acknowledged it (per-user clear: acknowledging removes it from
// only the acknowledger's view, never the shared board state). The "monitoring
// multiple rooms" gate is applied by the caller (countRoomsSupervisedBy).
export function unacknowledgedEmergenciesForUser(alerts = [], uid) {
  return emergenciesForUser(alerts, uid).filter(
    (a) => !(Array.isArray(a.acknowledgedBy) && a.acknowledgedBy.includes(uid))
  );
}

// How many rooms this user supervises right now — across the live room docs
// (supervisingDoctorId) and today's assignment sheet (attendingId), deduped by
// room. A supervisor "actively monitoring multiple rooms" (>1) gets the
// emergency popup; a doctor in a single room (doing a case) does not.
// ⚠️ TAKES THE VIEWER'S IDS, NOT A BARE UID. `rooms.supervisingDoctorId` and
// `assignment.attendingId` hold `staff/{id}` values; `user.uid` is a Firebase
// Auth id. Comparing them returned 0 for EVERY real supervising doctor, so
// `monitorsEmergencyPopup` was false and the centre-screen popup naming the room
// never rendered for them.
//
// The siren still fired — AlertContext resolves identity correctly — so the
// failure was the worst possible half-success: an anaesthesiologist covering
// four rooms hears a three-second emergency alarm and is shown NO ROOM NAME.
// Alarm without localisation is worse than either alone.
//
// Accepts a Set, an array, or a single id, so every caller can hand over
// whatever it holds. Fails closed: an empty set supervises nothing.
export function countRoomsSupervisedBy(rooms = [], assignments = [], who) {
  const ids =
    who instanceof Set ? who : new Set([].concat(who ?? []).filter(Boolean));
  if (ids.size === 0) return 0;
  const mine = (id) => !!id && ids.has(id);
  const roomIds = new Set();
  for (const r of rooms || []) {
    if (r && mine(r.supervisingDoctorId) && r.id) roomIds.add(r.id);
  }
  for (const a of assignments || []) {
    if (a && mine(a.attendingId)) roomIds.add(a.roomId || a.orRoom || a.id);
  }
  return roomIds.size;
}

// Is there already a LIVE emergency for this room? Used to block duplicate
// presses (the button flips to "Resolve" instead of firing a second alert).
export function hasLiveEmergencyForRoom(alerts = [], roomId) {
  if (!roomId) return false;
  return alerts.some(
    (a) => a.type === ALERT_TYPES.EMERGENCY && isLiveAlert(a) && a.roomId === roomId
  );
}

// ── Break-request derivation (Prompt 9) ──────
// Set of roomIds with a LIVE break request → those rooms blink yellow on every
// board + TV so any free provider can see a room needs coverage.
export function breakRequestRoomIds(alerts = []) {
  const ids = alerts
    .filter((a) => a.type === ALERT_TYPES.BREAK_REQUEST && isLiveAlert(a) && a.roomId)
    .map((a) => a.roomId);
  return new Set(ids);
}

// Is there already a LIVE break request for this room? (Blocks a duplicate.)
export function hasLiveBreakRequestForRoom(alerts = [], roomId) {
  if (!roomId) return false;
  return alerts.some(
    (a) => a.type === ALERT_TYPES.BREAK_REQUEST && isLiveAlert(a) && a.roomId === roomId
  );
}

// Live break requests to surface in a viewer's My Rooms. The room's supervising
// doctor (a targetId) always sees it; a FREE provider (no rooms of their own —
// isFree true) sees ALL of them so they can go cover. A provider busy in their
// own room only sees requests for rooms they're the doctor of.
export function breakRequestsForUser(alerts = [], uid, { isFree = false } = {}) {
  return alerts.filter(
    (a) =>
      a.type === ALERT_TYPES.BREAK_REQUEST &&
      isLiveAlert(a) &&
      (isFree || (Array.isArray(a.targetIds) && a.targetIds.includes(uid)))
  );
}

// ── Room-condition notifications (Phase 5.2) ─
// Live condition/handoff pings addressed to this viewer (the room's
// supervising doctor) — feeds the quiet notification strip in My Rooms.
export function conditionAlertsForUser(alerts = [], uid) {
  return alerts.filter(
    (a) =>
      a.type === ALERT_TYPES.ROOM_CONDITION &&
      isLiveAlert(a) &&
      Array.isArray(a.targetIds) &&
      a.targetIds.includes(uid)
  );
}

// Live condition alerts for a room (any recipient) — used to resolve them all
// when the underlying condition is cleared.
export function conditionAlertsForRoom(alerts = [], roomId) {
  if (!roomId) return [];
  return alerts.filter(
    (a) => a.type === ALERT_TYPES.ROOM_CONDITION && isLiveAlert(a) && a.roomId === roomId
  );
}

// Who may CLEAR (resolve) an emergency: ONLY the supervising doctor of THAT room.
// Every other doctor — including the AIC and other supervisors — can acknowledge
// but NOT clear; clearing is the room doctor's clinical judgement call. Admin is
// kept as a failsafe so a stuck alert can always be cleared. For a floor-wide
// emergency with no room doctor (roomDoctorId null), the AIC may clear it.
export function canClearEmergency(alert, { uid, isAIC, isAdmin } = {}) {
  if (!alert) return false;
  if (isAdmin) return true;                 // failsafe (never strands an alert)
  if (alert.roomDoctorId) return uid === alert.roomDoctorId; // that room's doctor only
  return !!isAIC;                           // floor-wide / no room doctor → AIC
}

// ──────────────────────────────────────
//  When was it raised?
//
//  An alert that has been blinking for two minutes and one that has been
//  blinking for forty are different situations, and the board could not tell
//  them apart. `sentAt` has been on every alert doc since they were first
//  written — it was simply never surfaced.
//
//  ⚠️ `sentAt` is a serverTimestamp(), which is NULL on the sending device
//  until the write round-trips. So "no timestamp yet" is a NORMAL transient
//  state on exactly the device that just raised the alert, not an error — it
//  degrades to no time rather than to "0 min" or 1970.
// ──────────────────────────────────────

// Firestore Timestamp | Date | epoch ms | null → Date | null. Never throws.
export function alertSentAt(alert) {
  const raw = alert?.sentAt ?? null;
  if (!raw) return null;
  if (typeof raw.toDate === "function") {
    try {
      return raw.toDate();
    } catch {
      return null;
    }
  }
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === "number") {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Firestore's {seconds,nanoseconds} plain shape (cached/serialised docs).
  if (typeof raw.seconds === "number") return new Date(raw.seconds * 1000);
  return null;
}

const pad2 = (n) => String(n).padStart(2, "0");

// Wall-clock time it was raised: "18:42". "" when unknown.
export function alertClockLabel(alert) {
  const d = alertSentAt(alert);
  if (!d) return "";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// How long it has been live: "just now", "3 min", "1 h 05 min".
// A clock skew that puts sentAt in the future reads "just now" rather than a
// negative age — the alert is live either way, and a minus sign helps nobody.
export function alertAgeLabel(alert, now = new Date()) {
  const d = alertSentAt(alert);
  if (!d) return "";
  const mins = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (!Number.isFinite(mins) || mins <= 0) return "just now";
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)} h ${pad2(mins % 60)} min`;
}

// The one-line "since" for a live alert: "18:42 · 7 min". Either half may be
// missing; the separator only appears when both exist.
export function alertSinceLabel(alert, now = new Date()) {
  const clock = alertClockLabel(alert);
  const age = alertAgeLabel(alert, now);
  return [clock, age].filter(Boolean).join(" · ");
}

// ── Who acknowledged it ──────────────────────
//
// `acknowledgedBy` is an array of UIDs, and per-user: acknowledging removes the
// alert from the acknowledger's own view only, never from the shared board
// (`unacknowledgedEmergenciesForUser` above). So on a shared screen "1 live
// emergency" says nothing about whether anyone has picked it up — which is the
// first thing the room wants to know.
//
// Returns { names, count, unnamed }:
//   names   — resolved full names, in acknowledgement order, deduped
//   count   — how many uids acknowledged, resolved or not
//   unnamed — how many could not be resolved to a person
//
// ⚠️ AN UNRESOLVABLE UID IS COUNTED, NOT DROPPED. Silently omitting one would
// render "acknowledged by Dr Hawk" for two acknowledgers, or an empty list for
// an alert that HAS been picked up — printing "nobody has this" while somebody
// is already walking to the room is the most dangerous thing this line could
// say. The caller reports the leftovers rather than inventing a name for them.
export function acknowledgerNames(alert, people = []) {
  const ids = Array.isArray(alert?.acknowledgedBy) ? alert.acknowledgedBy.filter(Boolean) : [];
  const pool = Array.isArray(people) ? people.filter(Boolean) : [];
  const seen = new Set();
  const names = [];
  let unnamed = 0;
  for (const uid of ids) {
    if (seen.has(uid)) continue;
    seen.add(uid);
    const person = pool.find((p) => p.uid === uid || p.id === uid);
    const name = person?.name ? String(person.name).trim() : "";
    if (name) names.push(name);
    else unnamed += 1;
  }
  return { names, count: seen.size, unnamed };
}

// Who is in the room, for stamping onto an emergency at send time.
//
// ⚠️ THE ID AND THE NAME MUST COME FROM THE SAME RECORD. RoomCard resolves
// `asg.ahpName || room.providerName`, and firestore.js:334 documents what that
// `||` costs: a DELIBERATELY cleared name on the assignment row falls straight
// through to a stale room doc and resurrects the person who was just relieved.
// On an emergency screen that would send a supervisor looking for a CRNA who
// left the room. So the source is chosen first, then both fields are read from
// it — never one from each.
//
// Precedence matches `resolveRoomDoctorId` / `resolveRoomCrnaId`: the live room
// doc wins, today's assignment row is the fallback. A missing name is null, so
// the receiving screen can say "not recorded" instead of printing a blank.
export function emergencyRoomStaff(room, assignment) {
  const r = room || {};
  const a = assignment || {};
  const doctorFromRoom = Boolean(r.supervisingDoctorId);
  const crnaFromRoom = Boolean(r.providerId);
  return {
    doctorId: r.supervisingDoctorId || a.attendingId || null,
    doctorName: (doctorFromRoom ? r.supervisorName : a.attendingId ? a.attendingName : null) || null,
    crnaId: r.providerId || a.ahpId || null,
    crnaName: (crnaFromRoom ? r.providerName : a.ahpId ? a.ahpName : null) || null,
  };
}
